import React from 'react';
import Immutable, { List, Map } from 'immutable';
import { Route } from 'react-router-dom';
import { Submit } from '../../../components/form/index';
import View from '../../../components/View';
import cache from '../../../core/cache/actions';
import qcache from '../../../core/cache/queries';
import comm from '../../../core/comm/actions';
import qcomm from '../../../core/comm/queries';
import { Header, Request, Response } from '../../../core/comm';
import form from '../../../core/components/form/actions';
import { bindActionCreatorsAndSubscribes, smash } from '../../../core/util';
import handshake from '../../../core/session/handshake/actions';
import qstorage from '../../../core/storage/queries';
import storage from '../../../core/storage/actions';
import RegistrationConfirmation from './RegistrationConfirmation';
import { applet, view, urls } from './core';

const messageKey = ['@ConfimationMessage'];

/**
 * --------------------------------------------------------
 *
 * Utilidades
 *
 * --------------------------------------------------------
 */

function getCid(props) {
  return Immutable.getIn(props, ['requestProps', 'match', 'params', 'cid']);
}

function setAvatar(data) {
  return storage.set(view, ['avatar'], data);
}

function getAvatar(state) {
  return qstorage.get(state, view, ['avatar']);
}

/**
 * --------------------------------------------------------
 *
 * Transformadores de dados
 *
 * --------------------------------------------------------
 */

/**
 * Retorna todos os dados necessários para as telas de perfil
 * e cadastro de formando.
 *
 * @param {object} data Dados retornados do banco.
 */
function transform(data) {
  const student = data.first();
  if (!student) {
    return data;
  }

  // As ruas, as vezes, vem com mais informações separadas por '-'.
  const address = student.get('endereco');
  const street = address ? address.split(' - ')[0] : '';
  return data.setIn([0, 'endereco'], street);
}

/**
 * Retorna os dados somente da seção de endereço.
 */
function transformCepData(data) {
  const d = data.first();
  if (!d) {
    return Map();
  }
  const description = d.get('descricao');
  const street = description ? description.split(' - ')[0] : '';

  return smash(
    d,
    ['cidade', 'nome'],
    ['estado', 'nome'],
    ['bairro', 'descricao'],
  )
    .set('endereco', street)
    .set('codLogradouro', d.get('codigo'));
}

/**
 * --------------------------------------------------------
 *
 * Ações
 *
 * --------------------------------------------------------
 */

function invalidateCache() {
  return cache.invalidate(applet, urls.get.confirmRegister);
}

/**
 * Retorna os possíveis gêneros do banco de dados.
 */
function fetchGenders() {
  return comm.getCombo(urls.combos.gender);
}

/**
 * Retorna os possíveis tipos de telefones.
 */
function fetchPhoneTypes() {
  return comm.getCombo(urls.combos.phoneTypes);
}

function routeTransformedData(data) {
  const student = data.get(0);
  if (student) {
    return form.merge(view, 'form', student.delete('avatar'));
  }

  const message = data.get(1);
  return storage.set(view, messageKey, message);
}

function fetchProfile(cid) {
  return comm.get(
    Request({
      view,
      url: urls.get.confirmRegister,
      urlParams: Map({ cid }),
      onSuccess: Response({
        dispatch: List([routeTransformedData]),
        transform,
      }),
      header: Header({
        include: List(['telefones']),
      }),
    }),
  );
}

function setCid(props) {
  const { view } = props;
  const cid = Immutable.getIn(props, [
    'requestProps',
    'match',
    'params',
    'cid',
  ]);
  return storage.set(view, ['cid'], cid);
}

function sendAvatarToDB(avatarFile) {
  return comm.post(
    Request({
      url: urls.post.saveAvatar,
      force: true,
      transform: file => {
        const formData = new FormData();
        formData.append('avatar', file);
        return formData;
      },
      onSuccess: Response({
        dispatch: List([handshake.init]),
      }),
      data: avatarFile,
    }),
  );
}

function sendToForm(data) {
  return form.merge(view, 'form', data);
}

/**
 * Consulta no banco os dados de um endereço de acordo com o CEP.
 */
function fetchAddressByCep(cep, view) {
  return comm.get(
    Request({
      view,
      url: urls.get.address,
      urlParams: Map({ cep }),
      onSuccess: Response({
        transform: transformCepData,
        dispatch: List([sendToForm]),
      }),
      header: Header({
        include: List(['cidade', 'estado', 'bairro', 'tipo']),
      }),
    }),
  );
}

function setTransform(data, avatar) {
  const newData = data.set('indEndCorreto', 1).set('avatar', avatar);
  if (newData.get('rg')) {
    return newData;
  }
  return newData.delete('rg');
}

/**
 * --------------------------------------------------------
 *
 * Queries
 *
 * --------------------------------------------------------
 */

function getCombos(state) {
  return {
    phoneTypes: qcache.getCombo(state, urls.combos.phoneTypes),
    gender: qcache.getCombo(state, urls.combos.gender),
  };
}

/**
 * --------------------------------------------------------
 *
 * Mapeamento
 *
 * --------------------------------------------------------
 */

function mapStateToProps(state, { view }) {
  const cid = qstorage.get(state, view, ['cid'], null);
  const avatar = getAvatar(state);
  return {
    combos: getCombos(state),
    communicating: qcomm.isCommunicating(state),
    isLoading: qcomm.isLoading(state, view, urls.get.confirmRegister),
    message: qstorage.get(state, view, messageKey),
    submitProfile: Submit({
      method: 'put',
      request: Request({
        url: urls.put.confirmRegister,
        urlParams: Map({ cid }),
        transform: data => setTransform(data, avatar),
        onSuccess: Response({
          dispatch: List([() => comm.refresh('/')]),
        }),
      }),
    }),
  };
}

function mapDispatchToProps(dispatch, ownProps) {
  const cid = getCid(ownProps);
  const actions = {
    sendAvatarToDB,
    invalidateCache,
    fetchAddressByCep,
    setAvatar,
  };
  const subs = {
    fetchProfile: () => fetchProfile(cid),
    fetchPhoneTypes,
    fetchGenders,
    setCid,
  };

  return bindActionCreatorsAndSubscribes(actions, subs, dispatch);
}

/**
 * --------------------------------------------------------
 *
 * Renderizadores
 *
 * --------------------------------------------------------
 */

const renderRegistrationConfirmation = View.createRenderer(
  {
    view,
    applet,
    component: RegistrationConfirmation,
  },
  mapStateToProps,
  mapDispatchToProps,
);

/**
 * --------------------------------------------------------
 *
 * Exportação
 *
 * --------------------------------------------------------
 */

export default (<Route exact path={view} render={renderRegistrationConfirmation} />);
